import {
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import { standardRequest } from 'shared/utils/request';
import {
  useWatch,
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  type ApplicationScore,
  type BaseCandidate,
  type BaseResume,
  type ResumeEmployment,
  type ResumeParsingStatus,
  type Row,
  type Scorecard,
} from 'talent_matching/types';

export type MyGreenhouseCandidate = BaseCandidate &
  Partial<ApplicationScore> & {
    matchedSkillsCount: number;
    scorecards?: Scorecard[];
    workPreferences: {
      languages?: string[];
      employmentTypes?: string[];
      worksites?: string[];
      location?: string | null;
      willingToRelocate: boolean;
    } | null;
    resume?:
      | (BaseResume & {
          details: {
            address: string | null;
            employments: ResumeEmployment[];
          };
          parsing_status?: ResumeParsingStatus;
        })
      | null;
  };

export type RankingPhase = 'gathering' | 'ranking' | 'finalizing';

export type RankingProgress = {
  phase: RankingPhase;
  collected?: number;
  completedChunks?: number;
  totalChunks?: number;
};

type MyGreenhouseQueryData = {
  rows: Row[];
  totalRows: number;
  inProgress: boolean;
  progress?: RankingProgress;
};

const useQueryParams = () => {
  const { control } = useFormContext();
  const formValues = useWatch({ control });

  const activeCompanyFilters = (
    (formValues.companyNameFilters ?? []) as Array<{
      id: string;
      mode: string;
      value: string;
    }>
  ).filter((f) => f.value?.trim());

  return {
    page: formValues.page as number,
    per_page: formValues.perPage as number,
    saved_for_later: formValues.savedForLater,
    can_have: formValues.canHave,
    must_have: formValues.mustHave,
    does_not_have: formValues.doesNotHave,
    years_of_experience_min: formValues.minExperience,
    years_of_experience_max: formValues.maxExperience,
    company_name_filters:
      activeCompanyFilters.length > 0 ? activeCompanyFilters : undefined,
    education_filters: formValues.educationFilters,
    work_preference_languages: formValues.workPreferenceLanguages,
    work_preference_employment_types: formValues.workPreferenceEmploymentTypes,
    work_preference_worksites: formValues.workPreferenceWorksites,
    work_preference_locations: formValues.workPreferenceLocations,
    work_preference_willing_to_relocate:
      formValues.workPreferenceWillingToRelocate,
  };
};

const IN_PROGRESS_POLL_INTERVAL_MS = 2000;

export const useMyGreenhouseCandidates = (
  path: string,
  { enabled = true }: { enabled?: boolean } = {}
): UseQueryResult<MyGreenhouseQueryData> => {
  const queryClient = useQueryClient();
  const params = useQueryParams();
  const { page, ...filterParams } = params;

  const queryKey = ['my-greenhouse-candidates', filterParams, page] as const;

  const queryResult = useQuery<MyGreenhouseQueryData>({
    queryKey,
    queryFn: async () => {
      const { data } = await standardRequest.get(path, {
        params: { ...filterParams, page },
      });

      if (data.in_progress) {
        const previous =
          queryClient.getQueryData<MyGreenhouseQueryData>(queryKey);
        const rawProgress = data.progress || {};
        const progress: RankingProgress = {
          phase: data.phase as RankingPhase,
          collected: rawProgress.collected,
          completedChunks: rawProgress.completed_chunks,
          totalChunks: rawProgress.total_chunks,
        };
        return {
          // Preserve previously fetched rows/totalRows so stale data stays
          // visible and interactive while the new ranking export runs.
          rows: previous?.rows ?? [],
          totalRows: previous?.totalRows ?? 0,
          inProgress: true,
          progress,
        };
      }

      const rows: Row[] = data.candidates.map(
        (candidate: MyGreenhouseCandidate) => {
          queryClient.setQueryData(
            ['my-greenhouse-candidate', candidate.id],
            candidate
          );
          return { id: candidate.id };
        }
      );

      return {
        rows,
        totalRows: data.total,
        inProgress: false,
      };
    },
    enabled,
    placeholderData: {
      rows: [],
      totalRows: 0,
      inProgress: false,
    },
    staleTime: 1000 * 60 * 10,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchInterval: (query) =>
      query.state.data?.inProgress ? IN_PROGRESS_POLL_INTERVAL_MS : false,
  });

  return queryResult;
};

export const useMyGreenhouseCandidate = (
  candidateId: number
): { candidate: MyGreenhouseCandidate | undefined } => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['my-greenhouse-candidate', candidateId],
    staleTime: Infinity,
    queryFn: () =>
      queryClient.getQueryData<MyGreenhouseCandidate>([
        'my-greenhouse-candidate',
        candidateId,
      ]),
    retry: 1,
  });

  return {
    candidate: query.data,
  };
};
